import React, { useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Icon from '@mui/material/Icon'
import CardMedia from '@mui/material/CardMedia'
import Skeleton from '@mui/material/Skeleton'

// Local imports
import AdxSortTable from 'components/reusable/table/AdxSortTable'
import { iconConstructor } from 'pages/approval-center/functions'
import defaultAvatar from 'assets/images/default_avatar.png'

// API services
import { useGetAllFilteredLeaveApproval } from 'services/api/modules/leave/leaveApproval'
import AdxAvatarSide from 'module/leave/pages/adminLeaveSetting/components/AdxAvatarSide'
import AdxEmptyStateV2 from 'components/reusable/emptyState/AdxEmptyStateV2'

const LeaveUpcomingTab = () => {
  const [limit, setLimit] = useState(5)
  const [page, setPage] = useState(0)
  const { data, isLoading, error } = useGetAllFilteredLeaveApproval({
    status: 'upcoming',
    limit,
    page: page + 1,
    sortBy: 'start_date',
    sortOrder: 'asc',
  })

  const handleChangePage = (e, newPage) => {
    setPage(newPage)
  }

  const handleChangeLimit = (e) => {
    setLimit(parseInt(e.target.value, 10))
    setPage(0)
  }

  const upcomingRequestColumn = [
    {
      Header: 'Request',
      accessor: 'leave_policy_name',
      Cell: (props) => {
        const data = props.row.original
        return (
          <Box
            display={'flex'}
            justifyContent={'space-between'}
            alignItems={'center'}
            columnGap={3}
          >
            <Box display="flex" alignItems="center" columnGap={2}>
              <Box
                display={'flex'}
                justifyContent={'center'}
                alignItems={'center'}
                width={50}
                height={50}
                bgcolor="alternate.light"
                borderRadius={'50%'}
              >
                <Icon sx={{ fontSize: 25, color: 'alternate.main' }}>
                  {iconConstructor(data?.leave_type === 'Leave Request' ? 1 : 2)}
                </Icon>
              </Box>
              <Box>
                <Typography
                  width={{
                    xs: 'calc(100vw - 400px)',
                    lg: 'calc(100vw - 1200px)',
                  }}
                  minWidth={{ xs: 1, lg: '200px' }}
                  noWrap
                  variant="body1"
                  color="dark-text.main "
                  textTransform="capitalize"
                >
                  {data?.leave_policy_name}
                </Typography>
                <Box display={'flex'} alignItems={'center'} columnGap={1.5}>
                  {data?.type ? (
                    <>
                      <Typography
                        noWrap
                        component="span"
                        variant="body2"
                        color="light-gray-text.main"
                        textTransform="capitalize"
                      >
                        {data?.type}
                      </Typography>

                      <Icon sx={{ fontSize: 7, color: 'light-gray-text.main' }}>
                        circle_rounded
                      </Icon>
                    </>
                  ) : null}
                  <Typography
                    noWrap
                    component="span"
                    variant="body2"
                    color="light-gray-text.main"
                    textTransform="capitalize"
                  >
                    {data?.start_date} - {data?.end_date}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )
      },
    },
    {
      Header: 'Amount',
      accessor: 'duration',
      Cell: (props) => {
        const data = props.row.original
        return (
          <Box display={'flex'} flexDirection="column" justifyContent="center" rowGap={0.5}>
            <Typography variant="body1" color="dark-text.main" align="left" noWrap>
              {data?.duration} {data?.duration === 1 ? 'day' : 'days'}
            </Typography>
          </Box>
        )
      },
    },
    {
      Header: 'Requester',
      accessor: 'request_from',
      Cell: (props) => {
        const data = props.row.original
        return (
          <Box display={'flex'} alignItems="center" columnGap={1}>
            <CardMedia
              component="img"
              sx={{
                height: 40,
                width: 40,
                bgcolor: 'gray-background.main',
                objectFit: 'cover',
                borderRadius: '50%',
                border: '3px solid',
                borderColor: 'white.main',
              }}
              alt={data?.request_from}
              src={data?.request_from_image || defaultAvatar}
            />

            <Typography variant="body1" color="dark-text.main" align="left" maxWidth={150} noWrap>
              {data?.request_from}
            </Typography>
          </Box>
        )
      },
    },
    {
      Header: 'Progress',
      accessor: 'layer_approver',
      Cell: (props) => {
        const data = props.row.original

        return (
          <Box display={'flex'} justifyContent="center" alignItems="center">
            <AdxAvatarSide
              tooltipMax={4}
              active
              data={data?.layer_approver?.map((layer) => ({
                name: layer.name,
                profile_image_url: layer.image,
              }))}
              // imageData={route.members}
            />
          </Box>
        )
      },
    },
  ]
  const tableData = useMemo(() => data, [data])
  const navigate = useNavigate()
  const backgroundLocation = useLocation()
  const hasUpcomingLeaves = (tableData?.data || []).length > 0

  if (isLoading) {
    return (
      <Box
        height={138}
        width={1}
        bgcolor="white.main"
        borderRadius={1}
        padding={{ xs: 2, lg: 3 }}
        mb={2}
        boxSizing="border-box"
        sx={{ boxShadow: 'none' }}
      >
        <Box height={1} display="flex" justifyContent="space-between" alignItems="center">
          <Box
            height={1}
            width={{ xs: 1, lg: 4 / 5 }}
            display="flex"
            alignItems="center"
            columnGap={2}
          >
            <Box display="flex" justifyContent="center" alignItems="center" width={90} height={90}>
              <Skeleton variant="circular" width={60} height={60} />
            </Box>
            <Box height={1} display="flex" flexDirection="column" justifyContent="space-between">
              <Box>
                <Skeleton variant="text" width={200} />
                <Skeleton variant="text" width={200} />
              </Box>
              <Skeleton variant="text" width={200} />
            </Box>
          </Box>
          <Skeleton variant="text" width={100} sx={{ display: { xs: 'none', lg: 'block' } }} />
        </Box>
      </Box>
    )
  }

  if (!hasUpcomingLeaves) {
    return (
      <AdxEmptyStateV2
        title={
          error?.status === 403
            ? 'You are not authorized to access this feature. Please contact your admin for access.'
            : 'Nothing to show here'
        }
      />
    )
  }
  return (
    <AdxSortTable
      nav="View"
      onClick={(e) => {
        navigate(`/app/approval-center/leave/request/${e.id}`, {
          state: {
            backgroundLocation,
            data: {
              id: e.id,
              type: e.leave_type === 'Leave Request' ? 1 : 2,
              title: e.leave_policy_name,
              subtitle: e.type,
              startDate: e.start_date,
              endDate: e.end_date,
              user: e.request_from,
              imgUrl: e.request_from_image,
              amount: e.duration,
              requestId: e.leave_request_id,
            },
          },
        })
      }}
      column={upcomingRequestColumn}
      data={tableData.data}
      sx={{
        marginRight: '10px',
        boxShadow: null,
        borderRadius: '3.5px',
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
      }}
      pagination={true}
      count={tableData?.total}
      page={page}
      onPageChange={handleChangePage}
      rowsPerPage={limit}
      onRowsPerPageChange={handleChangeLimit}
      height={'calc(100vh - 235px)'}
    />
  )
}

export default LeaveUpcomingTab
